import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { bool, func, shape } from 'prop-types';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';

import CustomerInformationAccordion from '../components/CustomerInformationAccordion';
import TerminalsAccordion from '../components/TerminalsAccordion';
import ProductsAccordion from '../components/ProductsAccordion';
import DestinationsAccordion from '../components/DestinationsAccordion';
import Carriers from './CarriersConnector';

const ViewDrawer = ({
  customerAuthorization,
  isFetchingCustomerAuthorization,
  isViewDrawerOpen,
  setCustomerAuthorization,
  setCustomerAuthorizationId,
  setCustomerAuthorizationToDelete,
  setIsViewDrawerOpen,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { isProductLoadingManage, isSupplier } = useUserRolePermissions();
  const {
    authorizationType = 'UNLIMITED',
    controlType = 'SINGLE',
    products = [],
    loadingControlNumbers = [],
    terminals = [],
  } = customerAuthorization;

  const handleClose = () => {
    setIsViewDrawerOpen(false);
    setCustomerAuthorizationToDelete({});
    setCustomerAuthorization({});
  };

  return (
    <Drawer
      anchor="right"
      onClose={() => handleClose()}
      open={isViewDrawerOpen}
      slotProps={{
        paper: {
          sx: {
            overflowY: 'hidden',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            width: { xs: 1, lg: 760 },
          },
        },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          boxShadow: 1,
          display: 'flex',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          zIndex: 1,
        }}
      >
        <Typography sx={{ color: 'text.primary', pt: '2px' }} variant="h6">
          View Authorization
        </Typography>
        <IconButton
          aria-label="closeIcon"
          onClick={() => handleClose()}
          sx={{
            alignSelf: 'flex-end',
          }}
        >
          <CloseRoundedIcon sx={{ color: 'primary.light' }} />
        </IconButton>
      </Box>
      {isFetchingCustomerAuthorization ? (
        <CircularProgress
          size={48}
          sx={{
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            left: { xs: 'calc(50% - 24px)', lg: 'calc(50% - 32px)' },
            position: 'absolute',
            top: 'calc(50% - 64px)',
          }}
        />
      ) : (
        <Box
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            },
            height: 'calc(100% - 129px)',
            overflowY: 'auto',
          }}
        >
          <CustomerInformationAccordion
            customerAuthorization={customerAuthorization}
            defaultExpanded={lgUp}
          />
          <TerminalsAccordion
            controlType={controlType}
            defaultExpanded={lgUp}
            terminals={terminals}
          />
          <ProductsAccordion
            authorizationType={authorizationType}
            defaultExpanded={lgUp}
            products={products}
          />
          <DestinationsAccordion
            defaultExpanded={lgUp}
            destinations={loadingControlNumbers}
            showItn={R.or(
              R.equals(authorizationType, 'ONETIME'),
              R.equals(authorizationType, 'ONELOAD')
            )}
          />
          <Carriers />
        </Box>
      )}
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: 'surface.white',
          bottom: 0,
          boxShadow: 0,
          display: 'flex',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          justifyContent: { xs: 'center', lg: 'flex-end' },
          position: 'fixed',
          px: 3,
          py: 2,
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          width: { xs: 1, lg: 760 },
          zIndex: 1,
        }}
      >
        {isProductLoadingManage && isSupplier ? (
          <>
            <Button
              onClick={() => {
                setCustomerAuthorizationId(customerAuthorization.customerAuthorizationId);
                handleClose();
              }}
              sx={{
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                width: { xs: 1, lg: 159 },
              }}
            >
              Edit
            </Button>
          </>
        ) : (
          <Button onClick={() => handleClose()}>Close</Button>
        )}
      </Box>
    </Drawer>
  );
};

ViewDrawer.propTypes = {
  customerAuthorization: shape({}).isRequired,
  isFetchingCustomerAuthorization: bool.isRequired,
  isViewDrawerOpen: bool.isRequired,
  setCustomerAuthorization: func.isRequired,
  setCustomerAuthorizationId: func.isRequired,
  setCustomerAuthorizationToDelete: func.isRequired,
  setIsViewDrawerOpen: func.isRequired,
};

export default ViewDrawer;
